import { useContext, useState, useEffect } from "react";
import { Row, FormGroup, Label, Input, Button } from "reactstrap";
import { SessionContext } from "../shared/session";
import Requester from "../shared/Requester";
import { catalogoMitigantes, buildRespuestasMitigantes } from "./CuestionarioConfig";

// Mapeo de tipo/actividad de sesión al identificador de actividad_vulnerable en BD
const MAPA_ACTIVIDAD_VULNERABLE = {
    'Correduría': 'fe_publica',
    'Mediador Privado': 'fe_publica',
};

const ETIQUETAS_FREQUENCY = [
    { valor: 1, label: '1 — Nunca o casi nunca' },
    { valor: 2, label: '2 — Raramente' },
    { valor: 3, label: '3 — Ocasionalmente' },
    { valor: 4, label: '4 — Frecuentemente' },
    { valor: 5, label: '5 — Muy frecuente' },
];

function normalizarId(id) {
    return String(id ?? '').trim();
}

function normalizarTipo(tipo) {
    return String(tipo ?? '').trim();
}

function parseDependenciaValor(valor) {
    const texto = String(valor ?? '').trim();
    if (texto === 'true') return true;
    if (texto === 'false') return false;
    const n = Number(texto);
    if (texto !== '' && !Number.isNaN(n)) return n;
    return texto;
}

function obtenerRespuestaPorId(respuestas, idPregunta) {
    const idBuscado = normalizarId(idPregunta);
    const clave = Object.keys(respuestas).find((k) => normalizarId(k) === idBuscado);
    return clave !== undefined ? respuestas[clave] : respuestas[idPregunta];
}

function respuestaCoincideDependencia(respuesta, dependenciaValor) {
    const esperado = parseDependenciaValor(dependenciaValor);
    if (typeof esperado === 'boolean') return respuesta === esperado;
    if (typeof esperado === 'number') return Number(respuesta) === esperado;
    return String(respuesta) === String(esperado);
}

function esPreguntaVisible(pregunta, respuestas) {
    const depId = normalizarId(pregunta.dependencia);
    if (!depId) return true;

    const depValor = pregunta.dependencia_valor;
    if (depValor === undefined || depValor === null || String(depValor).trim() === '') {
        return true;
    }

    const respuestaPadre = obtenerRespuestaPorId(respuestas, depId);
    if (respuestaPadre === undefined || respuestaPadre === '' || respuestaPadre === null) {
        return false;
    }

    return respuestaCoincideDependencia(respuestaPadre, depValor);
}

function resolverActividadVulnerable(tipo, actividad) {
    if (tipo === 'PLD' && actividad) {
        return MAPA_ACTIVIDAD_VULNERABLE[actividad] || actividad;
    }
    // Fallback a tipo para Correduría y Mediador Privado que utilizan el sistema completo
    return MAPA_ACTIVIDAD_VULNERABLE[tipo] || null;
}

function crearEstadoMitigantes() {
    const estado = {};
    catalogoMitigantes.forEach((item) => { estado[item.id] = ''; });
    return estado;
}

// ─── Campo de pregunta del cuestionario ─────────────────────────────────────
function CampoPregunta({ pregunta, valor, onChange }) {
    const { id, tipo } = pregunta;
    const tipoNorm = normalizarTipo(tipo);

    if (tipoNorm === 'integer') {
        return (
            <Input
                type="number"
                min={0}
                step={1}
                value={valor ?? ''}
                onChange={(e) => onChange(id, e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                style={{ maxWidth: 140 }}
            />
        );
    }

    if (tipoNorm === 'mix_10') {
        return (
            <Input
                type="number"
                min={0}
                max={10}
                step={1}
                value={valor ?? ''}
                onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (e.target.value === '') { onChange(id, ''); return; }
                    if (n >= 0 && n <= 10) onChange(id, n);
                }}
                style={{ maxWidth: 140 }}
            />
        );
    }

    if (tipoNorm === 'money') {
        return (
            <Input
                type="number"
                min={0}
                step={0.01}
                value={valor ?? ''}
                onChange={(e) => onChange(id, e.target.value === '' ? '' : parseFloat(e.target.value))}
                style={{ maxWidth: 200 }}
                placeholder="$ 0.00"
            />
        );
    }

    if (tipoNorm === 'frequency') {
        return (
            <div className="d-flex flex-column gap-1">
                {ETIQUETAS_FREQUENCY.map(({ valor: v, label }) => (
                    <FormGroup check key={v} className="mb-0">
                        <Label check>
                            <Input
                                type="radio"
                                name={`freq-${id}`}
                                value={v}
                                checked={valor === v}
                                onChange={() => onChange(id, v)}
                            />{' '}
                            {label}
                        </Label>
                    </FormGroup>
                ))}
            </div>
        );
    }

    if (tipoNorm === 'boolean') {
        return (
            <div className="d-flex flex-column gap-1">
                <FormGroup check className="mb-0">
                    <Label check>
                        <Input
                            type="radio"
                            name={`bool-${id}`}
                            checked={valor === true}
                            onChange={() => onChange(id, true)}
                        />{' '}
                        Sí
                    </Label>
                </FormGroup>
                <FormGroup check className="mb-0">
                    <Label check>
                        <Input
                            type="radio"
                            name={`bool-${id}`}
                            checked={valor === false}
                            onChange={() => onChange(id, false)}
                        />{' '}
                        No
                    </Label>
                </FormGroup>
            </div>
        );
    }

    // tipo no reconocido: texto libre
    return (
        <Input
            type="text"
            value={valor ?? ''}
            onChange={(e) => onChange(id, e.target.value)}
        />
    );
}

// ─── Componente principal ────────────────────────────────────────────────────
export default function DashboardConfig({ onGuardado }) {
    const session = useContext(SessionContext);
    const { tipo, actividad } = session?.unidad || {};

    // Paso 1: cuestionario  |  Paso 2: mitigantes visibles
    const [paso, setPaso] = useState(1);

    // Cuestionario de riesgo inherente (desde cat-cuestionarios)
    const [cuestionario, setCuestionario] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);
    const [respuestas, setRespuestas] = useState({});

    // Mitigantes
    const [mitigantesState, setMitigantesState] = useState(crearEstadoMitigantes);

    // Estado del guardado final
    const [guardando, setGuardando] = useState(false);
    const [errorGuardar, setErrorGuardar] = useState(null);

    const getDisplayName = (actividadVulnerable) => {
        switch (actividadVulnerable) {
            case 'inmobiliaria': return 'Desarrollo Inmobiliario';
            case 'fe_publica': return 'Fe Pública';
            default: return actividadVulnerable;
        }
    };

    useEffect(() => {
        const actividadVulnerable = resolverActividadVulnerable(tipo, actividad);
        if (!actividadVulnerable) { setCuestionario(null); return; }

        setCargando(true);
        setError(null);
        setPaso(1);
        setRespuestas({});
        setMitigantesState(crearEstadoMitigantes());

        Requester.get(`cat-cuestionarios?actividad_vulnerable=${actividadVulnerable}`)
            .then((res) => { setCuestionario(res?.data || null); })
            .catch(() => { setError('No se encontró el cuestionario para esta actividad vulnerable'); })
            .finally(() => { setCargando(false); });
    }, [tipo, actividad]);

    // ── Validaciones ──────────────────────────────────────────────────────────

    function todasCuestionarioRespondidas() {
        const preguntas = cuestionario?.cuestionario || [];
        return preguntas
            .filter((p) => esPreguntaVisible(p, respuestas))
            .every((p) => {
                const val = respuestas[p.id];
                if (normalizarTipo(p.tipo) === 'boolean') {
                    return val === true || val === false;
                }
                return val !== undefined && val !== '' && val !== null;
            });
    }

    function todasMitigantesRespondidas() {
        return catalogoMitigantes.every((item) => {
            const v = mitigantesState[item.id];
            return v !== undefined && v !== '';
        });
    }

    // ── Handlers ──────────────────────────────────────────────────────────────

    function handleCambio(id, valor) {
        setRespuestas((prev) => {
            const next = { ...prev, [id]: valor };
            (cuestionario?.cuestionario || []).forEach((p) => {
                if (!esPreguntaVisible(p, next)) {
                    delete next[p.id];
                }
            });
            return next;
        });
    }

    function handleMitiganteChange(id, valor) {
        setMitigantesState((prev) => ({ ...prev, [id]: valor }));
    }

    function handleContinuar(e) {
        e.preventDefault();
        if (!todasCuestionarioRespondidas()) return;
        setPaso(2);
        // Desplazar hacia la sección de mitigantes
        setTimeout(() => {
            document.getElementById('seccion-mitigantes')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }

    async function handleGuardarCompleto(e) {
        e.preventDefault();
        if (!todasMitigantesRespondidas()) return;

        setGuardando(true);
        setErrorGuardar(null);

        const cuestionarioConRespuestas = (cuestionario?.cuestionario || []).map((p) => {
            const visible = esPreguntaVisible(p, respuestas);
            return {
                id: p.id,
                dimension: p.dimension,
                pregunta: p.pregunta,
                tipo: p.tipo,
                scoring: p.scoring,
                peso_default: p.peso_default,
                criticidad: p.criticidad,
                dependencia: p.dependencia,
                dependencia_valor: p.dependencia_valor,
                respuesta: visible ? respuestas[p.id] : null,
            };
        });

        try {
            await Requester.post('respuestas-cuestionario', {
                actividad_vulnerable: cuestionario.actividad_vulnerable,
                unidad_id: session?.unidad?._id,
                cuestionario: cuestionarioConRespuestas,
                mitigantes: buildRespuestasMitigantes(mitigantesState),
            });
            if (onGuardado) onGuardado();
        } catch {
            setErrorGuardar('No se pudo guardar la evaluación. Intenta de nuevo.');
        } finally {
            setGuardando(false);
        }
    }

    // ── Render ────────────────────────────────────────────────────────────────

    const renderContenido = () => {
        if (cargando) return <p>Cargando cuestionario...</p>;
        if (error) return <p className="text-danger">{error}</p>;
        if (!cuestionario || !cuestionario.cuestionario?.length) {
            return <p>No se ha definido el cuestionario para este tipo de organización</p>;
        }

        return (
            <div className="w-100">
                {/* ── Sección 1: Cuestionario de riesgo inherente ── */}
                <form onSubmit={handleContinuar}>
                    <p className="mb-3">
                        <strong>Actividad Vulnerable:</strong> {getDisplayName(cuestionario.actividad_vulnerable)}
                    </p>

                    {cuestionario.cuestionario
                        .filter((p) => esPreguntaVisible(p, respuestas))
                        .map((p, idx) => (
                            <FormGroup key={p.id} className="mb-4 pb-3 border-bottom">
                                <Label className="fw-semibold">
                                    <span className="text-muted me-2">{idx + 1}.</span>
                                    {p.pregunta}
                                </Label>
                                <div className="mt-2">
                                    <CampoPregunta
                                        pregunta={p}
                                        valor={respuestas[p.id]}
                                        onChange={handleCambio}
                                    />
                                </div>
                                <div className="mt-1 d-flex gap-3">
                                    <small className="text-muted">Peso: {p.peso_default}</small>
                                    {p.criticidad && (
                                        <small className="text-muted">Criticidad: {p.criticidad}</small>
                                    )}
                                </div>
                            </FormGroup>
                        ))}

                    <div className="mt-3 mb-4">
                        <Button
                            type="submit"
                            color="primary"
                            disabled={!todasCuestionarioRespondidas() || paso === 2}
                        >
                            {paso === 2
                                ? '✓ Cuestionario completado'
                                : 'Continuar con la evaluación de mitigantes →'}
                        </Button>
                    </div>
                </form>

                {/* ── Sección 2: Mitigantes (aparece dinámicamente) ── */}
                {paso === 2 && (
                    <div id="seccion-mitigantes">
                        <hr />
                        <h5 className="mt-3 mb-1">
                            Sección 2: Evaluación de eficacia de mitigantes y controles internos
                        </h5>
                        <p className="text-muted mb-4">
                            Mide la efectividad porcentual de los controles implementados por la empresa (0% a 100%).
                        </p>

                        <form onSubmit={handleGuardarCompleto}>
                            {catalogoMitigantes.map((item) => (
                                <FormGroup tag="fieldset" key={item.id} className="mb-4">
                                    <legend className="fs-6 fw-semibold">{item.titulo}</legend>
                                    <p className="mb-1">{item.pregunta}</p>
                                    <small className="text-muted d-block mb-2">{item.ayuda}</small>
                                    {item.opciones.map((opcion) => (
                                        <FormGroup check key={opcion.value} className="mb-2">
                                            <Label check>
                                                <Input
                                                    type="radio"
                                                    name={item.id}
                                                    value={opcion.value}
                                                    checked={
                                                        mitigantesState[item.id] !== '' &&
                                                        Number(mitigantesState[item.id]) === opcion.value
                                                    }
                                                    onChange={() => handleMitiganteChange(item.id, opcion.value)}
                                                />
                                                {' '}{opcion.etiqueta}
                                            </Label>
                                        </FormGroup>
                                    ))}
                                </FormGroup>
                            ))}

                            {errorGuardar && (
                                <p className="text-danger mb-2">{errorGuardar}</p>
                            )}

                            <div className="d-flex gap-2 mt-3">
                                <Button
                                    type="button"
                                    color="link"
                                    className="p-0"
                                    onClick={() => setPaso(1)}
                                    disabled={guardando}
                                >
                                    ← Volver al cuestionario
                                </Button>
                                <Button
                                    type="submit"
                                    color="success"
                                    disabled={!todasMitigantesRespondidas() || guardando}
                                >
                                    {guardando ? 'Guardando...' : 'Guardar evaluación completa'}
                                </Button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <h3>Evaluación de Riesgos</h3>
            <Row>{renderContenido()}</Row>
        </div>
    );
}
